import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/saas/AuthContext";
import { useI18n, usePageTitle } from "@/i18n";
import { LanguageSelector } from "@/components/LanguageSelector";
import { errMsg } from "@/lib/saasApi";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  usePageTitle("titles.login");
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const u = await login(email, password);
      const dest = u.mustChangePassword ? "/account/password" : u.role === "OWNER_ADMIN" ? "/admin" : "/dashboard";
      nav(loc.state?.from?.pathname && !u.mustChangePassword ? loc.state.from.pathname : dest, { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-re-soft px-6" data-testid="login-page">
      <div className="w-full max-w-md rounded-3xl border border-re-line bg-white p-8 shadow-sm md:p-10">
        <div className="flex items-center justify-between"><Logo variant="light" emblemY={7} /><LanguageSelector /></div>
        <h1 className="mt-7 font-heading text-2xl font-bold text-re-charcoal">{t("auth.login.title")}</h1>
        <p className="mt-1 text-sm text-re-muted">{t("auth.login.sub")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t("auth.login.email")}</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus data-testid="login-email" className="border-re-line" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t("auth.login.password")}</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="border-re-line" />
          </div>
          {error && <p className="text-sm text-re-red" data-testid="login-error">{error}</p>}
          <Button type="submit" disabled={loading || !email || !password} data-testid="login-submit" className="w-full rounded-full bg-re-red py-6 text-base font-semibold text-white hover:bg-re-deepred">
            {loading ? t("auth.login.submitting") : t("auth.login.submit")}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-re-muted">
          {t("auth.login.noAccount")}{" "}
          <Link to="/register" data-testid="login-to-register" className="font-semibold text-re-red hover:underline">{t("auth.login.create")}</Link>
        </p>
      </div>
    </div>
  );
}
